// Schools database operations
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

// In-memory cache for school data (persists for app session)
const schoolCache = new Map();

export const schoolsDB = {
  // Get all schools (for signup dropdown)
  getSchools: async () => {
    try {
      const snapshot = await getDocs(collection(db, 'schools'));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting schools:', error);
      throw error;
    }
  },

  // Get single school (with caching)
  getSchool: async (schoolId) => {
    // Check cache first
    if (schoolCache.has(schoolId)) {
      console.log('📦 schoolsDB: Using cached school data for', schoolId);
      return schoolCache.get(schoolId);
    }

    try {
      console.log('📦 schoolsDB: Fetching school data from Firebase for', schoolId);
      const docRef = doc(db, 'schools', schoolId);
      const docSnap = await getDoc(docRef);
      const school = docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
      
      // Cache the result (even if null, to avoid repeated failed lookups)
      if (school) {
        schoolCache.set(schoolId, school);
        console.log('📦 schoolsDB: Cached school data for', schoolId);
      }
      
      return school;
    } catch (error) {
      console.error('Error getting school:', error);
      throw error;
    }
  },

  // Clear cache (useful for testing or forced refresh)
  clearCache: () => {
    schoolCache.clear();
    console.log('📦 schoolsDB: Cache cleared');
  },

  // Get all drills for a school
  getAllDrills: async (schoolId) => {
    try {
      const drillsRef = collection(db, 'schools', schoolId, 'drills');            
      const q = query(drillsRef, orderBy('order'));
      const snapshot = await getDocs(q);
      const drills = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      return drills;
    } catch (error) {
      console.error('❌ schoolsDB.getAllDrills: Error getting drills:', error);
      throw error;
    }
  },

  // Get drills for specific belt
  // NOTE: The relationship is one-way: Belts own drills (belts have drillIds array)
  // Drills do NOT have belt references - this keeps the data model simple and avoids sync issues
  getDrillsForBelt: async (schoolId, beltId) => {
    try {
      // Get belt document
      const beltDoc = await getDoc(doc(db, 'schools', schoolId, 'belts', beltId));
      if (!beltDoc.exists()) return [];
      
      const drillIds = beltDoc.data().drillIds || [];
      
      // Fetch all referenced drills
      const drills = [];
      for (const drillId of drillIds) {
        const drillDoc = await getDoc(doc(db, 'schools', schoolId, 'drills', drillId));
        if (drillDoc.exists()) {
          drills.push({ id: drillDoc.id, ...drillDoc.data() });
        }
      }
      
      return drills;
    } catch (error) {
      console.error('Error getting drills for belt:', error);
      throw error;
    }
  },

  // Get all belts for a school
  getBelts: async (schoolId) => {
    try {
      const beltsRef = collection(db, 'schools', schoolId, 'belts');
      const q = query(beltsRef, orderBy('order'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting belts:', error);
      throw error;
    }
  },

  // Get a single drill by id
  getDrill: async (schoolId, drillId) => {
    try {
      const drillDoc = await getDoc(doc(db, 'schools', schoolId, 'drills', drillId));
      return drillDoc.exists() ? { id: drillDoc.id, ...drillDoc.data() } : null;
    } catch (error) {
      console.error('Error getting drill:', error);
      throw error;
    }
  }
};

// Legacy exports for backward compatibility
export const getSchools = schoolsDB.getSchools;
export const getSchool = schoolsDB.getSchool;
export const getDrills = schoolsDB.getAllDrills;
export const getBelts = schoolsDB.getBelts;
